import type { ReactNode } from "react";
import { cn } from "@/lib/utils/cn";
import { ReportProblemButton } from "@/components/reports/ReportProblemButton";

export interface DetailPageHeaderProps {
  title: string;
  meta?: ReactNode[];
  /**
   * A second line under the title — an address, a policy period.
   *
   * Added for Bar & Tavern, which moved the insured's address up here so the page has ONE
   * header instead of a page title and an account strip repeating the name. `meta` renders
   * INLINE beside the title and could not hold an address without pushing everything else
   * off a laptop. Default undefined, so the other six consumers are unchanged.
   */
  subtitle?: ReactNode;
  actions?: ReactNode;
  reportProblem?: {
    applicationId: number;
    vertical: string;
    insuredName?: string | null;
    // Activity Timeline pre-fill (LIB-124). Supply for gateway-correlated
    // verticals (e.g. multi-rater) so the report carries the gateway task_id.
    taskId?: string | null;
    carrierDisplayName?: string | null;
    coverageType?: string | null;
    quoteData?: Record<string, unknown> | null;
  };
  sticky?: boolean;
  className?: string;
}

export function DetailPageHeader({
  title,
  meta,
  subtitle,
  actions,
  reportProblem,
  sticky = false,
  className = "",
}: DetailPageHeaderProps) {
  const hasActionArea = Boolean(reportProblem) || Boolean(actions);

  return (
    <div
      className={cn(
        "w-full bg-white border-b border-gray-200 px-6 py-4",
        sticky && "sticky top-0 z-20",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-4 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 whitespace-nowrap truncate">
              {title}
            </h1>
            {meta && meta.length > 0 && (
              <div className="flex items-center gap-3 text-sm text-gray-500 whitespace-nowrap">
                {meta.map((item, index) => (
                  <span key={index}>{item}</span>
                ))}
              </div>
            )}
          </div>
          {subtitle && (
            <p className="mt-0.5 truncate text-sm text-gray-500">{subtitle}</p>
          )}
        </div>
        {hasActionArea && (
          <div className="flex items-center gap-2 shrink-0">
            {reportProblem && (
              <ReportProblemButton
                applicationId={reportProblem.applicationId}
                vertical={reportProblem.vertical}
                insuredName={reportProblem.insuredName}
                taskId={reportProblem.taskId}
                carrierDisplayName={reportProblem.carrierDisplayName}
                coverageType={reportProblem.coverageType}
                quoteData={reportProblem.quoteData}
                size="sm"
              />
            )}
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
